import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Bug, Globe, User, Terminal, CheckCircle2, RotateCcw, Plus, Image as ImageIcon, Video, Eye, ExternalLink, Download, MessageSquare, Send, Clock, Search } from 'lucide-react';
import Content from '../../Components/Dashboard/Content/Content';
import GlobalBugReportModal from './GlobalBugReportModal';
import Modal_Component from '../../Components/ExtraComponents/Modal';

const getAttachmentUrl = (path) => {
    if (!path) return '';
    if (typeof path !== 'string') return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    let cleanPath = path.startsWith('/') ? path.substring(1) : path;
    if (!cleanPath.startsWith('uploads/')) {
        cleanPath = `uploads/${cleanPath}`;
    }
    return `https://ticket.tradestreet.in/backend/${cleanPath}`;
};

const isImageFile = (url) => {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
    return cleanUrl.endsWith('.png') || cleanUrl.endsWith('.jpg') || cleanUrl.endsWith('.jpeg') || cleanUrl.endsWith('.gif') || cleanUrl.endsWith('.webp') || cleanUrl.endsWith('.svg');
};

const isVideoFile = (url) => {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
    return cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.ogg') || cleanUrl.endsWith('.mov');
};

const ReportBug = () => {
    const user_details = JSON.parse(localStorage.getItem("user_details"));
    const user_role = JSON.parse(localStorage.getItem("user_role"));

    const [formData, setFormData] = useState({ domain: window.location.hostname });

    const [loading, setLoading] = useState(false);
    const [bugs, setBugs] = useState([]);
    const [bugsLoading, setBugsLoading] = useState(true);
    const [filterType, setFilterType] = useState(user_role === 'ADMIN' ? 'all' : 'my'); // 'all' or 'my'
    const [showModal, setShowModal] = useState(false);
    const [selectedBug, setSelectedBug] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Chat states
    const [bugChats, setBugChats] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isSendingChat, setIsSendingChat] = useState(false);
    const [chatsLoading, setChatsLoading] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [ticketTypeFilter, setTicketTypeFilter] = useState("");

    const fetchBugs = async (type = filterType) => {
        setBugsLoading(true);

        try {
            let url = `https://ticket.tradestreet.in/backend/api/common/project-bugs?domain=${window.location.hostname}`;

            const currentUserId = user_details?.user_id || user_details?._id;
            const token = user_details?.token || localStorage.getItem("token");
            if (ticketTypeFilter) {
                url += `&ticketType=${encodeURIComponent(ticketTypeFilter)}`;
            }

            // If user is ADMIN but wants to see only their bugs, we mask the role 
            // so the backend API filters by userId instead of returning all bugs.
            const effectiveRole = (user_role === 'ADMIN' && type === 'my') ? 'USER' : user_role;

            const config = {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...(token && { "x-access-token": token })
                }
            };

            if (effectiveRole) config.headers['role'] = effectiveRole;
            if (currentUserId) config.headers['userid'] = currentUserId;


            const response = await axios.get(url, config);


            if (response?.data?.success || response?.data?.status) {
                setBugs(response.data.data || []);
            } else {
                setBugs([]);
            }

        } catch (error) {
            console.error("Error fetching bugs:", error);
            setBugs([]);
        } finally {
            setBugsLoading(false);
        }
    };
    useEffect(() => {
        fetchBugs(filterType);
    }, [filterType, ticketTypeFilter]);

    const filteredBugs = bugs.filter(bug => {
        let match = true;
        if (statusFilter && bug.status !== statusFilter) match = false;

        if (startDate) {
            const bugDate = new Date(bug.createdAt);
            const sDate = new Date(startDate);
            sDate.setHours(0, 0, 0, 0);
            if (bugDate < sDate) match = false;
        }

        if (endDate) {
            const bugDate = new Date(bug.createdAt);
            const eDate = new Date(endDate);
            eDate.setHours(23, 59, 59, 999);
            if (bugDate > eDate) match = false;
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const titleMatch = bug.title?.toLowerCase().includes(query);
            const ticketIdMatch = bug.ticketId?.toLowerCase().includes(query);
            const typeMatch = bug.ticketType?.toLowerCase().includes(query);
            const statusMatch = bug.status?.toLowerCase().includes(query);
            if (!titleMatch && !ticketIdMatch && !typeMatch && !statusMatch) match = false;
        }

        return match;
    });

    // form submission moved to GlobalBugReportModal

    const [approvingId, setApprovingId] = useState(null);

    const handleApprove = async (bugId) => {
        try {
            setApprovingId(bugId);
            let url = `https://ticket.tradestreet.in/backend/api/common/panel/approve-bug/${bugId}`;

            const token = user_details?.token || localStorage.getItem("token");


            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "x-access-token": token,
                    ...(user_role && { role: user_role })
                }
            };

            // Assuming the approve endpoint uses PUT or POST. Using PUT here as it's an update.
            const response = await axios.put(url, {}, config);
            if (response?.data?.success || response?.data?.status) {
                toast.success('Bug approved successfully');
                fetchBugs(); // Refresh the list
            } else {
                toast.error(response?.data?.message || 'Failed to approve bug');
            }
        } catch (error) {
            console.error("Error approving bug:", error);
            toast.error('Error approving bug. Please try again.');
        } finally {
            setApprovingId(null);
        }
    };

    const fetchBugChats = async (bugId) => {
        setChatsLoading(true);
        try {
            const token = user_details?.token || localStorage.getItem("token");
            const config = {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...(token && { "x-access-token": token })
                }
            };
            const response = await axios.get(`https://ticket.tradestreet.in/backend/api/common/panel/bug-chat/${bugId}`, config);
            if (response?.data?.success || response?.data?.status) {
                setBugChats(response.data.data || []);
            } else {
                setBugChats([]);
            }
        } catch (error) {
            console.error("Error fetching bug chats:", error);
            setBugChats([]);
        } finally {
            setChatsLoading(false);
        }
    };

    const handleSendChat = async (e) => {
        e.preventDefault();
        if (!chatInput.trim() || !selectedBug) return;
        setIsSendingChat(true);
        try {
            const token = user_details?.token || localStorage.getItem("token");
            const config = {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...(token && { "x-access-token": token })
                }
            };
            const payload = {
                message: chatInput,
                userName: user_details?.UserName || user_details?.userName || 'User',
                role: user_role
            };
            const response = await axios.post(`https://ticket.tradestreet.in/backend/api/common/panel/bug-chat/${selectedBug._id}`, payload, config);
            if (response?.data?.success || response?.data?.status) {
                setChatInput('');
                fetchBugChats(selectedBug._id);
            } else {
                toast.error(response?.data?.message || 'Failed to send message');
            }
        } catch (error) {
            console.error("Error sending chat:", error);
            toast.error("Failed to send message");
        } finally {
            setIsSendingChat(false);
        }
    };

    const handleViewDetails = (bug) => {
        setSelectedBug(bug);
        setShowDetailModal(true);
        fetchBugChats(bug._id);
    };

    const getPriorityColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'critical': return 'bg-danger text-white';
            case 'high': return 'bg-warning text-dark';
            case 'medium': return 'bg-info text-white';
            default: return 'bg-secondary text-white';
        }
    };

    return (
        <Content button_status={false} >
            <div className="container-fluid p-0">
                <div className="row g-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                            <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 wrapper-data rounded-3 me-3">
                                        <Bug size={24} className="text-info" />
                                    </div>
                                    <div>
                                        <h4 className="mb-0 fw-bold">Project Bugs</h4>
                                        <p className="text-muted small mb-0">Active reports for {formData.domain}</p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <div className="btn-group btn-group-sm p-1 bg-light rounded-pill me-2">
                                        {user_role === 'ADMIN' && (
                                            <button
                                                className={`btn rounded-pill px-3 border-0 ${filterType === 'all' ? 'btn-white shadow-sm' : 'text-muted'}`}
                                                onClick={() => setFilterType('all')}
                                            >
                                                All Bugs
                                            </button>
                                        )}
                                        <button
                                            className={`btn rounded-pill px-3 border-0 ${filterType === 'my' ? 'btn-white shadow-sm' : 'text-muted'}`}
                                            onClick={() => setFilterType('my')}
                                        >
                                            My Bugs
                                        </button>
                                    </div>
                                    <button className="btn btn-sm btn-light border rounded-circle p-2 d-flex align-items-center justify-content-center me-2" onClick={() => fetchBugs()}>
                                        <RotateCcw size={14} />
                                    </button>
                                    <button className="btn btn-primary px-3 d-flex align-items-center gap-2" style={{ borderRadius: '10px' }} onClick={() => setShowModal(true)}>
                                        <Plus size={18} />
                                        <span>Add Bug</span>
                                    </button>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                {/* Filters Row */}
                                <div className="p-3 border-bottom d-flex flex-wrap gap-3 align-items-center bg-light bg-opacity-50">
                                    <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white border-end-0">
                                                <Search size={14} className="text-muted" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 ps-0 shadow-none"
                                                placeholder="Search by title, ID or type..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ minWidth: '130px' }}>
                                        <select
                                            className="form-select form-select-sm shadow-none"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Working">Working</option>
                                            <option value="Resolved">Resolved</option>
                                            <option value="Resolved & Tested">Resolved & Tested</option>
                                            <option value="Reopened">Reopened</option>
                                        </select>
                                    </div>
                                    <div style={{ minWidth: "160px" }}>
                                        <select
                                            className="form-select form-select-sm shadow-none"
                                            value={ticketTypeFilter}
                                            onChange={(e) => setTicketTypeFilter(e.target.value)}
                                        >
                                            <option value="">All Types</option>
                                            <option value="Bug">Bug</option>
                                            <option value="Add IP">Add IP</option>
                                            <option value="Add License">Add License</option>
                                            <option value="update">updation</option>
                                        </select>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="date"
                                            className="form-control form-control-sm shadow-none"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                        <span className="text-muted small">to</span>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm shadow-none"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {bugsLoading ? (
                                    <div className="p-5 text-center">
                                        <div className="spinner-border text-primary"></div>
                                        <p className="mt-2 text-muted">Fetching bugs...</p>
                                    </div>
                                ) : filteredBugs.length === 0 ? (
                                    <div className="p-5 text-center">
                                        <div className="bg-light p-4 rounded-circle d-inline-block mb-3">
                                            <CheckCircle2 size={48} className="text-success opacity-50" />
                                        </div>
                                        <h5 className="text-muted">No bugs found!</h5>
                                        <p className="small text-muted">Try adjusting your filters or search query.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4 py-3">#</th>
                                                    <th className="ps-4 py-3">Issue</th>
                                                    {/* <th className="py-3">Module</th> */}
                                                    <th className="ps-4 py-3">Ticket Type</th>
                                                    {/* <th className="py-3">Reporter</th> */}
                                                    <th className="py-3">Priority</th>
                                                    <th className="py-3">Status</th>
                                                    <th className="py-3">username</th>
                                                    <th className="py-3">Reported At</th>
                                                    <th className="pe-4 py-3 text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredBugs.map((bug, index) => (
                                                    <tr key={bug._id || index}>
                                                        <td className="text-center fw-semibold">
                                                            {index + 1}
                                                        </td>
                                                        <td className="ps-4">
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-bold text-dark">{bug.title}</span>
                                                                <span className="text-muted small text-truncate" style={{ maxWidth: '300px' }}>{bug.description}</span>
                                                                {/* <span className="text-muted" style={{ fontSize: '10px' }}>{bug?.ticketType?.slice(-6).toUpperCase() || 'NEW'}</span> */}
                                                            </div>
                                                        </td>
                                                        {/* <td>
                                                            <span className="badge bg-light text-muted fw-normal border">{bug.moduleName || 'General'}</span>
                                                        </td> */}
                                                        {/* <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar-xs bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px', fontSize: '10px' }}>
                                                                    {bug.userName?.charAt(0).toUpperCase() || 'U'}
                                                                </div>
                                                                <span className="small">{bug.userName}</span>
                                                            </div>
                                                        </td> */}
                                                        <td>
                                                            <span className={`badge rounded-pill ${getPriorityColor(bug.priority)} px-2 py-1`} style={{ fontSize: '10px' }}>
                                                                {bug.ticketType}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className={`badge rounded-pill ${getPriorityColor(bug.priority)} px-2 py-1`} style={{ fontSize: '10px' }}>
                                                                {bug.priority}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className={`badge ${bug.status === 'Resolved' ? 'bg-success' : 'bg-light text-primary border'} px-2 py-1`}>
                                                                {bug.status || 'Pending'}
                                                            </div>
                                                            {bug.isAdminApproved && <div className="badge bg-success bg-opacity-10 text-success border border-success px-2 py-1 ms-1">Approved</div>}
                                                        </td>
                                                        <td>
                                                            <span className="small text-muted">
                                                                {bug.sourceUserName}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="small text-muted">
                                                                {new Date(bug.createdAt).toLocaleDateString()}
                                                            </span>
                                                        </td>
                                                        <td className="pe-4 text-end">
                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary rounded-pill px-3 d-inline-flex align-items-center gap-1"
                                                                    onClick={() => handleViewDetails(bug)}
                                                                >
                                                                    {/* <Eye size={14} /> */}
                                                                    <span>View </span>
                                                                </button>
                                                                {user_role === 'ADMIN' && !bug.isAdminApproved && (
                                                                    <button
                                                                        className="btn btn-sm btn-success rounded-pill px-3"
                                                                        onClick={() => handleApprove(bug._id)}
                                                                        disabled={approvingId === bug._id}
                                                                    >
                                                                        {approvingId === bug._id ? 'Approving...' : 'Approve'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Bug Modal */}
            <GlobalBugReportModal
                isOpen={showModal}
                onClose={(success) => {
                    setShowModal(false);
                    if (success) fetchBugs();
                }}
            />

            {/* Bug Details Modal */}
            <Modal_Component
                isOpen={showDetailModal}
                handleClose={() => setShowDetailModal(false)}
                title={`Bug Details - #${selectedBug?._id?.slice(-6).toUpperCase() || 'DETAILS'}`}
                hideBtn={true}
                size="xl"
            >
                {selectedBug && (
                    <div className="bug-details-container p-1">
                        {/* Bug Header Card */}
                        <div className="card border-0 bg-white mb-4 shadow-sm" style={{ borderRadius: '16px', borderLeft: `6px solid ${selectedBug.priority === 'Critical' ? '#dc3545' : selectedBug.priority === 'High' ? '#fd7e14' : selectedBug.priority === 'Medium' ? '#0dcaf0' : '#198754'}` }}>
                            <div className="card-body p-4">
                                <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                            <span className={`badge px-3 py-1.5 rounded-pill fw-semibold ${getPriorityColor(selectedBug.priority)}`} style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                                {selectedBug.priority} Priority
                                            </span>
                                            <span className={`badge px-3 py-1.5 rounded-pill fw-semibold ${selectedBug.status === 'Resolved' ? 'bg-success text-white' : 'bg-light text-primary border border-primary-subtle'}`} style={{ fontSize: '11px' }}>
                                                {selectedBug.status || 'Pending'}
                                            </span>
                                            {selectedBug.isAdminApproved && (
                                                <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle px-3 py-1.5 rounded-pill fw-semibold" style={{ fontSize: '11px' }}>
                                                    Admin Approved
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="fw-bold text-dark mb-2 lh-sm">{selectedBug.title}</h3>
                                        <div className="d-flex align-items-center text-muted small flex-wrap gap-2">
                                            <div className="d-flex align-items-center gap-1">
                                                <User size={14} className="text-secondary" />
                                                <span>Reported by <strong>{selectedBug.userName}</strong> ({selectedBug.role || 'User'})</span>
                                            </div>
                                            <span className="text-muted">•</span>
                                            <span>{new Date(selectedBug.createdAt).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <span className="font-monospace text-secondary small bg-light px-3 py-2 rounded-3 border fw-semibold" style={{ fontSize: '12px' }}>
                                            TICKET #{selectedBug._id?.slice(-6).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-4">
                            {/* Left Main Content */}
                            <div className="col-lg-12 d-flex">
                                {/* Description Card */}
                                <div className="card border-0 shadow-sm mb-4 col-lg-6" style={{ borderRadius: '16px' }}>
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <h5 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2" style={{ fontSize: '16px' }}>
                                            <Bug size={18} className="text-primary" />
                                            <span>Description</span>
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="p-3 bg-light bg-opacity-50 rounded-3 border text-dark " style={{ minHeight: '140px', whiteSpace: 'pre-line', fontSize: '14px', lineHeight: '1.6' }}>
                                            {selectedBug.description}
                                        </div>
                                    </div>
                                </div>

                                {/* Error Stack Card */}
                                {selectedBug.errorStack && (
                                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
                                        <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                            <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: '16px' }}>
                                                <Terminal size={18} className="text-danger" />
                                                <span>Error Stack</span>
                                            </h5>
                                        </div>
                                        <div className="card-body p-4">
                                            <div className="mock-terminal shadow-sm rounded-3 overflow-hidden">
                                                <div className="mock-terminal-header d-flex align-items-center justify-content-between px-3 py-2 bg-dark bg-opacity-90 border-bottom border-secondary border-opacity-20">
                                                    <div className="d-flex gap-1">
                                                        <span className="dot dot-red"></span>
                                                        <span className="dot dot-yellow"></span>
                                                        <span className="dot dot-green"></span>
                                                    </div>
                                                    <span className="text-muted font-monospace" style={{ fontSize: '11px' }}>error_stack.log</span>
                                                </div>
                                                <pre className="bg-dark p-3 m-0 overflow-auto font-monospace" style={{ maxHeight: '250px', fontSize: '12px', color: '#ff6b6b' }}>
                                                    <code>{selectedBug.errorStack}</code>
                                                </pre>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Attachments Card */}
                                <div className="card border-0 shadow-sm  col-lg-6" style={{ borderRadius: '16px' }}>
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: '16px' }}>
                                            <ImageIcon size={18} className="text-primary" />
                                            <span>Attachments</span>
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        {selectedBug.attachments && selectedBug.attachments.length > 0 ? (
                                            <div className="row g-3">
                                                {selectedBug.attachments.map((file, idx) => {
                                                    const fileUrl = getAttachmentUrl(file);
                                                    const isImage = isImageFile(file);
                                                    const isVideo = isVideoFile(file);

                                                    return (
                                                        <div key={idx} className="col-md-6">
                                                            <div className="card h-100 border border-light-subtle rounded-4 overflow-hidden shadow-sm attachment-card">
                                                                {isImage ? (
                                                                    <div className="position-relative bg-light d-flex align-items-center justify-content-center overflow-hidden attachment-media-container" style={{ height: '200px' }}>
                                                                        <img
                                                                            src={fileUrl}
                                                                            alt={`Attachment ${idx + 1}`}
                                                                            className="img-fluid w-100 h-100 object-fit-cover attachment-img"
                                                                            onError={(e) => {
                                                                                e.target.onerror = null;
                                                                                e.target.src = 'https://placehold.co/600x400?text=Image+Load+Failed';
                                                                            }}
                                                                        />
                                                                        <div className="attachment-overlay d-flex align-items-center justify-content-center">
                                                                            <a
                                                                                href={fileUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-light btn-sm rounded-pill px-3 shadow fw-semibold d-flex align-items-center gap-1"
                                                                                style={{ zIndex: 2 }}
                                                                            >
                                                                                <ExternalLink size={12} />
                                                                                <span>View Full</span>
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                ) : isVideo ? (
                                                                    <div className="bg-black d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                                                        <video
                                                                            src={fileUrl}
                                                                            controls
                                                                            className="w-100 h-100 object-fit-contain"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="d-flex flex-column align-items-center justify-content-center p-4 bg-light text-center" style={{ height: '200px' }}>
                                                                        <Terminal size={36} className="text-secondary mb-2" />
                                                                        <span className="small text-truncate w-100 px-3 mb-3 fw-medium">{file.split('/').pop()}</span>
                                                                        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary rounded-pill px-4 shadow-sm d-flex align-items-center gap-1">
                                                                            <Download size={14} />
                                                                            <span>Download File</span>
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                <div className="card-body py-2 px-3 bg-white border-top-0 d-flex align-items-center justify-content-between">
                                                                    <span className="small text-muted text-uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                                        {isImage ? 'Image' : isVideo ? 'Video' : 'Attachment'} #{idx + 1}
                                                                    </span>
                                                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-link btn-sm p-0 text-decoration-none small d-flex align-items-center gap-1">
                                                                        <ExternalLink size={11} />
                                                                        <span>Link</span>
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-center p-5 border border-dashed rounded-4 bg-light bg-opacity-25">
                                                <ImageIcon size={32} className="text-muted opacity-50 mb-2" />
                                                <p className="text-muted small mb-0">No visual attachments uploaded for this bug.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Chat Section */}

                            </div>
                            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
                                <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                    <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: '16px' }}>
                                        <MessageSquare size={18} className="text-primary" />
                                        <span>Discussion</span>
                                    </h5>
                                </div>
                                <div className="card-body p-4 d-flex flex-column" style={{ minHeight: '350px', maxHeight: '500px' }}>
                                    {/* Chat Messages */}
                                    <div className="chat-messages flex-grow-1 overflow-auto mb-3 pe-2" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        {chatsLoading ? (
                                            <div className="d-flex justify-content-center align-items-center h-100 w-100 text-muted">
                                                <div className="spinner-border spinner-border-sm me-2"></div> Loading chats...
                                            </div>
                                        ) : bugChats.length === 0 ? (
                                            <div className="d-flex flex-column justify-content-center align-items-center h-100 w-100 text-muted bg-light rounded-3 p-4">
                                                <MessageSquare size={32} className="opacity-50 mb-2" />
                                                <p className="mb-0 small">No messages yet. Start the conversation!</p>
                                            </div>
                                        ) : (
                                            bugChats.map((chat, idx) => {
                                                const isMe = chat.userName === (user_details?.UserName || user_details?.userName || 'User');
                                                return (
                                                    <div key={idx} className={`d-flex flex-column ${isMe ? 'align-items-end' : 'align-items-start'}`}>
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            {/* <span className="fw-semibold" style={{ fontSize: '12px', color: isMe ? '#0d6efd' : '#495057' }}>
                                                                    {isMe ? 'You' : chat.userName || 'Unknown'}
                                                                </span> */}
                                                            {chat.role && !isMe && (
                                                                <span className="badge bg-light text-secondary border px-2 py-0" style={{ fontSize: '9px' }}>
                                                                    {chat.role}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div
                                                            className={`px-3 py-2 rounded-3 shadow-sm ${isMe ? 'bg-primary text-white' : 'bg-white border'}`}
                                                            style={{ maxWidth: '80%', fontSize: '13px', borderBottomRightRadius: isMe ? '4px' : '12px', borderBottomLeftRadius: !isMe ? '4px' : '12px' }}
                                                        >
                                                            {chat.message}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-1 mt-1 text-muted" style={{ fontSize: '10px' }}>
                                                            <Clock size={10} />
                                                            <span>{new Date(chat.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Chat Input Area */}
                                    <div className="mt-auto">
                                        <form onSubmit={handleSendChat} className="position-relative">
                                            <textarea
                                                className="form-control rounded-4 pe-5 shadow-sm border-light-subtle"
                                                rows="2"
                                                placeholder="Type a message..."
                                                value={chatInput}
                                                onChange={(e) => setChatInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendChat(e);
                                                    }
                                                }}
                                                style={{ resize: 'none', fontSize: '14px', backgroundColor: '#f8f9fa' }}
                                            />
                                            <button
                                                type="submit"
                                                className="btn btn-primary rounded-circle p-2 position-absolute shadow-sm"
                                                style={{ bottom: '10px', right: '10px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                disabled={isSendingChat || !chatInput.trim()}
                                            >
                                                {isSendingChat ? <div className="spinner-border spinner-border-sm text-white" /> : <Send size={16} />}
                                            </button>
                                        </form>
                                        <div className="text-muted mt-1 ms-1" style={{ fontSize: '10px' }}>Press Enter to send, Shift+Enter for new line</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Admin Approve Section */}
                        {user_role === 'ADMIN' && !selectedBug.isAdminApproved && (
                            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                <button
                                    className="btn btn-success px-4 py-2.5 shadow-sm d-flex align-items-center gap-2"
                                    style={{ borderRadius: '10px', fontWeight: '600' }}
                                    onClick={() => {
                                        handleApprove(selectedBug._id);
                                        setSelectedBug(prev => ({ ...prev, isAdminApproved: true }));
                                    }}
                                    disabled={approvingId === selectedBug._id}
                                >
                                    {approvingId === selectedBug._id ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm" role="status"></div>
                                            <span>Approving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 size={16} />
                                            <span>Approve Bug Ticket</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Modal_Component>

            <style dangerouslySetInnerHTML={{
                __html: `
                .btn-white {
                    background-color: #fff;
                    color: #0d6efd;
                }
                .hover-bg-light:hover {
                    background-color: #f8f9fa;
                    cursor: pointer;
                }
                .transition-all {
                    transition: all 0.2s ease-in-out;
                }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .form-control:focus, .form-select:focus {
                    background-color: #fff !important;
                    border-color: #0d6efd !important;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.1) !important;
                }
                .avatar-xs {
                    width: 24px;
                    height: 24px;
                }
                .table thead th {
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    font-weight: 700;
                    color: #6c757d;
                }
                .object-fit-cover {
                    object-fit: cover;
                }
                .object-fit-contain {
                    object-fit: contain;
                }
                .attachment-card {
                    transition: transform 0.25s ease, box-shadow 0.25s ease;
                }
                .attachment-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
                }
                .attachment-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.4);
                    opacity: 0;
                    transition: opacity 0.25s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .attachment-card:hover .attachment-overlay {
                    opacity: 1;
                }
                .attachment-img {
                    transition: transform 0.4s ease;
                }
                .attachment-card:hover .attachment-img {
                    transform: scale(1.05);
                }
                .metadata-item-card {
                    background-color: #f8f9fa;
                    transition: background-color 0.2s;
                }
                .metadata-item-card:hover {
                    background-color: #f1f3f5;
                }
                .max-w-100 {
                    max-width: 100%;
                }
                .dot {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    display: inline-block;
                }
                .dot-red { background-color: #ff5f56; }
                .dot-yellow { background-color: #ffbd2e; }
                .dot-green { background-color: #27c93f; }
            ` }} />
        </Content>
    );
};

export default ReportBug;
